import React, {createContext, useContext, useState, useCallback} from 'react';
import {toast, ToastOptions, UpdateOptions} from 'react-toastify';
import {NotificationItem} from 'types';

type INotificationContext = ReturnType<typeof useNotificationContextBuilder>;

const NotificationContext = createContext<INotificationContext>({} as INotificationContext);

export const Msg = {} as {
   notify: INotificationContext['notify'];
   update: INotificationContext['update'];
};

const useNotificationContextBuilder = () => {
   const [notifications, setNotifications] = useState<NotificationItem[]>([]);

   const notify = useCallback(
      (message: string, type: NotificationItem['type'] = 'info', options?: ToastOptions) => {
         const id = toast[type](message, options);

         setNotifications((prev) => [
            {
               id,
               message,
               type,
               createdAt: Date.now(),
               read: false,
            },
            ...prev,
         ]);

         return id;
      },
      []
   );

   const update = (id: NotificationItem['id'], options?: UpdateOptions) => {
      setNotifications((prev) =>
         prev.map((n) =>
            n.id === id
               ? {
                    ...n,
                    message: options?.render as string,
                    type: options?.type ? (options.type as NotificationItem['type']) : n.type,
                 }
               : n
         )
      );
      toast.update(id, options);
   };

   const markAsRead = (id: NotificationItem['id']) => {
      setNotifications((prev) => prev.filter((n) => n.id !== id));
   };

   const clearAll = () => setNotifications([]);

   Msg.notify = notify;
   Msg.update = update;
   return {notifications, notify, markAsRead, clearAll, update};
};

export const NotificationProvider = ({children}: {children: React.ReactNode}) => {
   const value = useNotificationContextBuilder();

   return <NotificationContext.Provider value={value}>{children}</NotificationContext.Provider>;
};

export const useNotifications = () => useContext(NotificationContext);
